import React from "react";
import { cn } from "@/lib/utils";
import { Package, Calendar, ArrowUpDown, Clock, Hash } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type MaterialGroupMode = "PO" | "DATE";
export type MaterialSortKey =
  | "orderId_desc"
  | "orderId_asc"
  | "deadline_desc"
  | "deadline_asc"
  | "createdAt_desc";

interface MaterialPlanningGroupingSwitchProps {
  groupByMode: MaterialGroupMode;
  onGroupByChange: (mode: MaterialGroupMode) => void;
  sortBy: "orderId" | "deadline" | "createdAt";
  sortOrder: "asc" | "desc";
  onSortChange: (sortBy: "orderId" | "deadline" | "createdAt", sortOrder: "asc" | "desc") => void;
}

export function MaterialPlanningGroupingSwitch({
  groupByMode,
  onGroupByChange,
  sortBy,
  sortOrder,
  onSortChange,
}: MaterialPlanningGroupingSwitchProps) {
  const currentSortKey: MaterialSortKey = `${sortBy}_${sortOrder}` as MaterialSortKey;

  const handleSelectSort = (val: string) => {
    switch (val) {
      case "orderId_desc":
        onSortChange("orderId", "desc");
        break;
      case "orderId_asc":
        onSortChange("orderId", "asc");
        break;
      case "deadline_desc":
        onSortChange("deadline", "desc");
        break;
      case "deadline_asc":
        onSortChange("deadline", "asc");
        break;
      case "createdAt_desc":
        onSortChange("createdAt", "desc");
        break;
      default:
        onSortChange("orderId", "desc");
    }
  };

  return (
    <div className="flex items-center gap-2.5 flex-wrap">
      {/* Cụm Switch Nhóm Theo Lệnh vs Nhóm Theo Ngày */}
      <div className="inline-flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200 shadow-2xs">
        <button
          type="button"
          onClick={() => onGroupByChange("PO")}
          className={cn(
            "px-2.5 py-1 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5 cursor-pointer select-none",
            groupByMode === "PO"
              ? "bg-white text-teal-700 shadow-2xs font-bold"
              : "text-slate-600 hover:text-slate-900"
          )}
          title="Gom nhóm nhu cầu vật tư theo từng Lệnh sản xuất"
        >
          <Package className="w-3.5 h-3.5" />
          <span>Theo Lệnh SX</span>
        </button>

        <button
          type="button"
          onClick={() => onGroupByChange("DATE")}
          className={cn(
            "px-2.5 py-1 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5 cursor-pointer select-none",
            groupByMode === "DATE"
              ? "bg-white text-teal-700 shadow-2xs font-bold"
              : "text-slate-600 hover:text-slate-900"
          )}
          title="Gom nhóm nhu cầu vật tư theo ngày cần kèm dòng Subtotal"
        >
          <Calendar className="w-3.5 h-3.5" />
          <span>Theo Ngày Cần VT</span>
        </button>
      </div>

      {/* Dropdown Sắp Xếp Nhu Cầu Vật Tư */}
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] font-semibold text-slate-500 flex items-center gap-1">
          <ArrowUpDown className="w-3 h-3 text-slate-400" />
          Sắp xếp:
        </span>
        <Select value={currentSortKey} onValueChange={handleSelectSort}>
          <SelectTrigger className="h-8 text-xs w-[205px] bg-white border-slate-200 shadow-2xs">
            <SelectValue placeholder="Chọn kiểu sắp xếp..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="orderId_desc" className="text-xs">
              <span className="flex items-center gap-1.5">
                <Hash className="w-3 h-3 text-teal-600" />
                Số lệnh LSX (Mã PO Giảm dần)
              </span>
            </SelectItem>
            <SelectItem value="orderId_asc" className="text-xs">
              <span className="flex items-center gap-1.5">
                <Hash className="w-3 h-3 text-slate-400" />
                Số lệnh LSX (Mã PO Tăng dần)
              </span>
            </SelectItem>
            <SelectItem value="deadline_desc" className="text-xs">
              <span className="flex items-center gap-1.5">
                <Clock className="w-3 h-3 text-teal-600" />
                Hạn giao PO (Mới nhất DESC)
              </span>
            </SelectItem>
            <SelectItem value="deadline_asc" className="text-xs">
              <span className="flex items-center gap-1.5">
                <Clock className="w-3 h-3 text-rose-500" />
                Hạn giao PO (Gấp nhất ASC)
              </span>
            </SelectItem>
            <SelectItem value="createdAt_desc" className="text-xs">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3 h-3 text-slate-500" />
                Ngày tạo lệnh (Mới nhất)
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
